/**
 * icons.js
 * Mapeo de categorías a nombres de íconos (basado en Lucide React).
 */

// Mapeo explícito para categorías principales y excepciones específicas
const DIRECT_MAPPING = {
  // --- ALIMENTOS FRESCOS ---
  "Produce": "Carrot",
  "Fruits": "Apple",
  "Vegetables": "Carrot",
  "Garlic": "Ratatouille", // O "Carrot" si no tienes uno específico
  "Ajo (Garlic)": "Carrot",
  "Avocados": "Apple",

  // --- CARNES Y MARISCOS ---
  "Meats": "Beef",
  "Beef": "Beef",
  "Pork": "Beef", // Icono genérico de carne
  "Chicken": "Drumstick",
  "Seafood": "Fish",
  "Fish Fillets": "Fish",
  "Shrimp": "Fish",
  "Deli Meat": "Sandwich",
  "Sausage": "Sandwich",

  // --- LÁCTEOS Y HUEVOS ---
  "Dairy & Cheese": "Milk",
  "Dairy & Alternatives": "Milk",
  "Milk": "Milk",
  "Cheese": "Cheese", // Si existe en tu set, si no "Circle"
  "Yogurt": "Milk",
  "Butter": "Milk",
  "Eggs": "Egg",

  // --- PANADERÍA Y REPOSTERÍA ---
  "Bakery": "Croissant",
  "Bread": "Croissant",
  "Baked Goods": "Croissant",
  "Baking": "ChefHat",
  "Cake Mix": "Cake",
  "Cookies": "Cookie",
  "Desserts": "Cake",
  "Donuts": "Donut",

  // --- DESPENSA ---
  "Pantry": "ShoppingBasket",
  "Canned Goods": "Soup",
  "Soups": "Soup",
  "Broth": "Soup",
  "Pasta": "UtensilsCrossed",
  "Rice": "Utensils",
  "Cereals": "Wheat",
  "Spices": "Shaker", // Salero/Pimentero
  "Adobo (All Purpose Seasoning)": "Shaker",
  "Condiments": "Utensils",
  "Oils": "Droplet",
  "Olive Oil": "Droplet",

  // --- SNACKS ---
  "Snacks": "Pizza", // O "Cookie"
  "Chips": "Pizza",
  "Candy": "Candy",
  "Chocolate": "Candy",
  "Popcorn": "Popcorn",
  "Nuts": "Nut",

  // --- CONGELADOS ---
  "Frozen Foods": "Snowflake",
  "Ice Cream": "IceCream",

  // --- BEBIDAS ---
  "Beverages": "CupSoda",
  "Water": "GlassWater",
  "Soda": "CupSoda",
  "Juice": "CupSoda",
  "Coffee": "Coffee",
  "Tea": "Coffee",
  "Energy Drinks": "Zap",

  // --- ALCOHOL ---
  "Alcohol": "Martini",
  "Beer": "Beer",
  "Wines": "Wine",
  "Spirits": "Martini",
  "Liquor": "Martini",
  "Rum": "Martini",
  "Whisky": "Martini",
  "Vodka": "Martini",

  // --- BEBÉ ---
  "Baby": "Baby",
  "Baby Diapers": "Baby",
  "Baby Wipes": "Baby",
  "Baby Formula/Nutritional Drinks": "Milk",

  // --- MASCOTAS ---
  "Pet Supplies": "PawPrint",
  "Dog Treats": "Bone",
  "Cat Food": "PawPrint",

  // --- HOGAR Y LIMPIEZA ---
  "Household": "Home",
  "Cleaning Supplies": "SprayCan",
  "Laundry Detergent": "Droplets", // O "Shirt"
  "Dish Soap (Liquid)": "Droplets",
  "Bleach": "AlertTriangle", // O "SprayCan"
  "Trash Bags": "Trash2",
  "Toilet Paper": "Scroll", // O un icono de papel
  "Paper Towels": "Scroll",
  "Air Fresheners": "Wind",

  // --- SALUD Y CUIDADO PERSONAL ---
  "Health": "HeartPulse",
  "Personal Care": "Heart",
  "Vitamins": "Pill",
  "Supplements": "Pill",
  "Pain Relief": "Pill",
  "Incontinence Pads": "Shield",
  "Feminine Care": "Heart",
  "Shampoo": "Droplet",
  "Soap (Liquid)": "Droplet",
  "Toothpaste": "Smile",

  // --- AUTO Y OTROS ---
  "Auto": "Car",
  "Electronics": "Smartphone",
  "Batteries": "Battery",
  "AA/AAA Batteries": "Battery",
  "Toys": "Gamepad2", // O "ToyBrick"
  "Gardening Supplies": "Shovel",
  "Party Supplies": "PartyPopper"
};

/**
 * Función helper para obtener el ícono basado en la categoría o nombre del producto.
 * Realiza una búsqueda directa y luego una búsqueda por palabras clave.
 * * @param {string} key - La llave de la categoría (ej. "Red Wine")
 * @returns {string} - El nombre del icono (ej. "Wine")
 */
export const getIconName = (key) => {
  if (!key) return "ShoppingBasket";

  // 1. Búsqueda exacta en el mapa
  if (DIRECT_MAPPING[key]) {
    return DIRECT_MAPPING[key];
  }

  // 2. Normalizar texto para búsqueda inteligente
  const lowerKey = key.toLowerCase();

  // 3. Lógica de palabras clave (Keywords)

  // Alcohol
  if (lowerKey.includes('wine') || lowerKey.includes('vino') || lowerKey.includes('champagne')) return "Wine";
  if (lowerKey.includes('beer') || lowerKey.includes('cerveza')) return "Beer";
  if (lowerKey.includes('rum') || lowerKey.includes('whisky') || lowerKey.includes('vodka') || lowerKey.includes('tequila') || lowerKey.includes('gin') || lowerKey.includes('liqueur')) return "Martini";

  // Bebidas
  if (lowerKey.includes('coffee') || lowerKey.includes('café') || lowerKey.includes('espresso')) return "Coffee";
  if (lowerKey.includes('tea') || lowerKey.includes('té')) return "Coffee";
  if (lowerKey.includes('juice') || lowerKey.includes('jugo') || lowerKey.includes('nectar') || lowerKey.includes('lemonade')) return "CupSoda";
  if (lowerKey.includes('water') || lowerKey.includes('agua')) return "GlassWater";
  if (lowerKey.includes('soda') || lowerKey.includes('cola') || lowerKey.includes('soft drink')) return "CupSoda";

  // Lácteos
  if (lowerKey.includes('milk') || lowerKey.includes('leche') || lowerKey.includes('cream')) return "Milk";
  if (lowerKey.includes('cheese') || lowerKey.includes('queso')) return "Cheese"; // Fallback a 'Circle' si no tienes icono de queso
  if (lowerKey.includes('yogurt') || lowerKey.includes('yogur')) return "Milk";

  // Panadería
  if (lowerKey.includes('bread') || lowerKey.includes('pan') || lowerKey.includes('bun') || lowerKey.includes('roll')) return "Croissant";
  if (lowerKey.includes('cake') || lowerKey.includes('bizcocho') || lowerKey.includes('muffin') || lowerKey.includes('pie')) return "Cake";
  if (lowerKey.includes('cookie') || lowerKey.includes('galleta')) return "Cookie";

  // Carnes
  if (lowerKey.includes('beef') || lowerKey.includes('steak') || lowerKey.includes('meat') || lowerKey.includes('pork') || lowerKey.includes('jamón') || lowerKey.includes('ham')) return "Beef";
  if (lowerKey.includes('chicken') || lowerKey.includes('pollo') || lowerKey.includes('turkey') || lowerKey.includes('pavo')) return "Drumstick";
  if (lowerKey.includes('fish') || lowerKey.includes('pescado') || lowerKey.includes('salmon') || lowerKey.includes('tuna') || lowerKey.includes('shrimp') || lowerKey.includes('marisco')) return "Fish";

  // Frescos
  if (lowerKey.includes('fruit') || lowerKey.includes('fruta') || lowerKey.includes('apple') || lowerKey.includes('berry') || lowerKey.includes('grape')) return "Apple";
  if (lowerKey.includes('vegetable') || lowerKey.includes('vegetal') || lowerKey.includes('salad') || lowerKey.includes('lettuce') || lowerKey.includes('onion') || lowerKey.includes('tomato')) return "Carrot";

  // Congelados
  if (lowerKey.includes('frozen') || lowerKey.includes('congelado') || lowerKey.includes('ice')) return "Snowflake";

  // Limpieza
  if (lowerKey.includes('clean') || lowerKey.includes('limpia') || lowerKey.includes('detergent') || lowerKey.includes('soap') || lowerKey.includes('bleach')) return "SprayCan";
  if (lowerKey.includes('trash') || lowerKey.includes('basura') || lowerKey.includes('bag')) return "Trash2";
  if (lowerKey.includes('paper') || lowerKey.includes('papel') || lowerKey.includes('towel') || lowerKey.includes('tissue')) return "Scroll";

  // Bebé
  if (lowerKey.includes('baby') || lowerKey.includes('bebé') || lowerKey.includes('diaper') || lowerKey.includes('pañal')) return "Baby";

  // Mascotas
  if (lowerKey.includes('dog') || lowerKey.includes('cat') || lowerKey.includes('pet') || lowerKey.includes('perro') || lowerKey.includes('gato')) return "PawPrint";

  // Auto
  if (lowerKey.includes('auto') || lowerKey.includes('car') || lowerKey.includes('oil') || lowerKey.includes('tire')) return "Car";

  // Salud
  if (lowerKey.includes('vitamin') || lowerKey.includes('supplement') || lowerKey.includes('pain') || lowerKey.includes('medicine')) return "Pill";

  // Default
  return "ShoppingBasket";
};